import { Component, OnInit } from '@angular/core';
import { CybersocxdrService } from '../../../shared/services/xtendedsoc/cybersocxdr.service';
import { CaseXDR } from 'src/app/shared/model/cybersocxdr/microsocticket';
import { constants } from '../../../shared/constants';
import { CybersocXDRDashboardSearchCriteria } from '../../../shared/model/searchCriteria';
import FileSaver from 'file-saver';
import { AlertService } from 'src/app/shared/services/alert.service';
import { XtendedFiltersService } from '../../xtendedfilters.service';
import { XtendedSearchable } from '../../pages/xtended-page';
import { XtendedHighchartService } from '../../xtended-highchart.service';
import { XtendedFacet, XtendedGroupbyFacet } from 'src/app/shared/model/cybersocxdr/facets';
import { RepartitionChartItem } from 'src/app/shared/model/cybersocxdr/charts/common';
import { Severity } from 'src/app/shared/model/cybersocxdr/facetsValues';

@Component({
  selector: 'app-xtended-incidents-page',
  templateUrl: './incidents.component.html',
  styleUrls: ['./incidents.component.scss'],
})
export class XtendedSOCIncidentsComponent implements OnInit, XtendedSearchable {
  public scrollLoading = false;

  currentPage: number;
  page: string;

  // data
  searched: boolean;
  cases: CaseXDR[];
  casesAll: CaseXDR[];
  backlogCases: CaseXDR[];
  casesCount: number;
  backlogCasesCount: number;
  casesLoaded: boolean;
  createdFrom: string;
  createdTo: string;
  casesCountPrettier: string;
  closedCasesCount: number;
  closedCasesCountPrettier: string;
  backlogCasesCountPrettier: string;
  groupedIncidents: Highcharts.SeriesPieOptions[];
  incidentsCounts: {
    [key: string]: {
      periodTotal: number;
      previousPeriodTotal: number;
      difference: number;
    };
  };

  limit: number;
  caseNumber: string;

  severitiesCount = [Severity.CRITICAL, Severity.HIGH, Severity.MEDIUM, Severity.LOW];

  incidentsByEntityData: RepartitionChartItem[];

  //filters
  groupBy: XtendedGroupbyFacet = XtendedFacet.SEVERITY;
  groupByOptions: XtendedGroupbyFacet[] = [
    XtendedFacet.SEVERITY,
    XtendedFacet.OWNER,
    XtendedFacet.XDR_STATUS,
    XtendedFacet.CATEGORIES,
  ];

  flags: number[] = [];

  sortedBy: string;
  sortedType: string;

  lockedLoading: boolean;

  incidentsFlagged: CaseXDR[];

  constructor(
    private cybersocxdrService: CybersocxdrService,
    private globalFilters: XtendedFiltersService,
    private alertService: AlertService,
    public xtendedHighchartService: XtendedHighchartService
  ) {}

  public loadNextCases() {
    if (
      !this.cases ||
      this.cases.length >= this.casesCount ||
      this.lockedLoading ||
      (this.page !== 'backlog' && this.page !== 'all') ||
      this.scrollLoading
    ) {
      return;
    }

    this.scrollLoading = true;
    this.globalFilters.searchCriteria.skip = this.cases.length;

    if (this.page === 'backlog') {
      this.cybersocxdrService
        .getCases({ ...this.globalFilters.searchCriteria, status: ['-closed'] })
        .then((cases: CaseXDR[]) => {
          this.cases = this.cases.concat(cases);
          this.scrollLoading = false;
        });
    } else {
      this.cybersocxdrService.getCases(this.globalFilters.searchCriteria).then((cases: CaseXDR[]) => {
        this.cases = this.cases.concat(cases);
        this.scrollLoading = false;
      });
    }
  }

  async onSearch() {
    const searchCriteria = this.globalFilters.searchCriteria;
    this.createdTo = searchCriteria.createdTo;
    this.createdFrom = searchCriteria.createdFrom;
    this.searched = true;

    searchCriteria.limit = this.limit;
    searchCriteria.skip = 0;

    if (this.sortedBy) {
      searchCriteria.sortBy = {
        field: this.sortedBy,
        type: this.sortedType,
      };
    }

    this.groupedIncidents = [
      this.xtendedHighchartService.pieChartSerie(
        await this.cybersocxdrService.getCasesGroupBy(searchCriteria, this.groupBy),
        this.groupBy
      ),
    ];

    this.incidentsByEntityData = await this.cybersocxdrService.getCasesGroupByOvertime(
      searchCriteria,
      XtendedFacet.INCIDENT_DETAILED_STATUS
    );

    this.backlogCasesCount = (
      await this.cybersocxdrService.countCases({ ...searchCriteria, status: ['-closed'] })
    ).incidentCount;
    this.casesCount = (await this.cybersocxdrService.countCases(searchCriteria)).incidentCount;

    this.casesAll = await this.cybersocxdrService.getCases(searchCriteria);
    this.backlogCases = await this.cybersocxdrService.getCases({ ...searchCriteria, status: ['-closed'] });

    this.closedCasesCount = this.casesCount - this.backlogCasesCount;
    this.closedCasesCountPrettier = Number(this.closedCasesCount).toLocaleString();

    this.casesCountPrettier = Number(this.casesCount).toLocaleString();
    this.backlogCasesCountPrettier = Number(this.backlogCasesCount).toLocaleString();
    this.casesLoaded = true;
    this.lockedLoading = false;

    const insights = await this.cybersocxdrService.getClientInsights(searchCriteria);

    for (let insight of insights) {
      switch (insight.title) {
        case 'Volume low severity':
          this.incidentsCounts = {
            ...this.incidentsCounts,
            low: {
              periodTotal: insight.periodTotal,
              previousPeriodTotal: insight.previousPeriodTotal,
              difference: insight.periodTotal - insight.previousPeriodTotal,
            },
          };
          break;
        case 'Volume medium severity':
          this.incidentsCounts = {
            ...this.incidentsCounts,
            medium: {
              periodTotal: insight.periodTotal,
              previousPeriodTotal: insight.previousPeriodTotal,
              difference: insight.periodTotal - insight.previousPeriodTotal,
            },
          };
          break;
        case 'Volume high severity':
          this.incidentsCounts = {
            ...this.incidentsCounts,
            high: {
              periodTotal: insight.periodTotal,
              previousPeriodTotal: insight.previousPeriodTotal,
              difference: insight.periodTotal - insight.previousPeriodTotal,
            },
          };
          break;
        case 'Volume critical severity':
          this.incidentsCounts = {
            ...this.incidentsCounts,
            critical: {
              periodTotal: insight.periodTotal,
              previousPeriodTotal: insight.previousPeriodTotal,
              difference: insight.periodTotal - insight.previousPeriodTotal,
            },
          };
          break;
      }
    }

    this.loadCases();
  }

  async exportCases() {
    const searchCriteria = this.globalFilters.searchCriteria;

    if (this.sortedBy) {
      searchCriteria.sortBy = {
        field: this.sortedBy,
        type: this.sortedType,
      };
    }

    (await this.cybersocxdrService.casesCSVExport(searchCriteria)).subscribe({
      next: (val: any) => {
        const file = new Blob([val], { type: 'text/csv' });
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
          (window.navigator as any).msSaveOrOpenBlob(file);
        } else {
          FileSaver.saveAs(file, 'incidents.csv');
        }
      },
      error: async (err) => {
        const message = JSON.parse(await err.error.text()).message;
        this.alertService.handlerError(message);
      },
    });
  }

  async searchCase(event) {
    const { type, value } = event;
    if (type === 'caseNumber') {
      const singleCase = await this.cybersocxdrService.getCase(parseInt(value, 10));
      this.cases = [];
      if (singleCase) {
        this.cases = [singleCase];
      }
      return;
    }
    if (type === 'subject') {
      this.cases = await this.cybersocxdrService.getCasesBySubject(value);
      this.casesCountPrettier = Number(this.cases.length).toLocaleString();
      this.lockedLoading = true;
    }
  }

  async ngOnInit() {
    this.lockedLoading = false;
    this.currentPage = 1;
    this.page = 'backlog';
    this.casesLoaded = true;
    this.casesCount = 0;
    this.limit = constants.recordsPerPage.microsocticket;
    this.flags = await this.cybersocxdrService.getFlaggedCasesId();
    this.incidentsFlagged = await this.cybersocxdrService.getFlaggedCases(this.flags);
    this.onSearch();
  }

  async updateGroupBy(groupBy: XtendedGroupbyFacet): Promise<void> {
    this.groupBy = groupBy;
    this.groupedIncidents = [
      this.xtendedHighchartService.pieChartSerie(
        await this.cybersocxdrService.getCasesGroupBy(this.globalFilters.searchCriteria, groupBy),
        groupBy
      ),
    ];
  }

  loadCases() {
    this.casesLoaded = false;
    switch (this.page) {
      case 'all':
        this.cases = this.casesAll;
        break;
      case 'backlog':
        this.cases = this.backlogCases;
        break;
      case 'flagged':
        this.cases = this.incidentsFlagged;
        break;
      default:
        this.cases = [];
        break;
    }
    this.casesLoaded = true;
  }

  async updateCaseSearch(searchCriteria: CybersocXDRDashboardSearchCriteria): Promise<void> {
    this.casesLoaded = false;
    searchCriteria.limit = this.limit;
    this.cases = await this.cybersocxdrService.getCases(searchCriteria);
    this.casesCount = (await this.cybersocxdrService.countCases(searchCriteria)).incidentCount;
    this.casesLoaded = true;
  }

  async onFlag(caseRef: number) {
    const flagIndex = this.flags.findIndex((flag) => flag === caseRef);
    if (flagIndex === -1) {
      await this.cybersocxdrService.flagIncident(caseRef);
      this.flags.push(caseRef);
    } else {
      await this.cybersocxdrService.deleteFlagIncident(caseRef);
      this.flags.splice(flagIndex, 1);
    }
    console.log(this.flags);
    this.incidentsFlagged = await this.cybersocxdrService.getFlaggedCases(this.flags);
    this.loadCases();
  }

  async sortCases(sortBy: string) {
    this.casesLoaded = false;
    this.sortedBy = sortBy;
    this.sortedType = this.sortedType === 'asc' ? 'desc' : 'asc';
    this.onSearch();
    this.casesLoaded = true;
  }
}
